import React from 'react';
import { Alert, AlertTitle, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';

interface ErrorBannerProps {
  error: string;
  onDismiss: () => void;
  severity?: 'error' | 'warning' | 'info' | 'success';
  title?: string;
}

/**
 * ErrorBanner - Reusable error/notification banner component
 * 
 * A flexible banner component for displaying errors, warnings, and notifications
 * across the application. Supports different severity levels and custom titles.
 * 
 * @param error - The error message to display
 * @param onDismiss - Function to call when the banner is dismissed
 * @param severity - The severity level (error, warning, info, success)
 * @param title - Custom title for the banner (defaults based on severity)
 */
const ErrorBanner: React.FC<ErrorBannerProps> = ({ 
  error, 
  onDismiss, 
  severity = 'error',
  title
}) => {
  const defaultTitles = {
    error: 'Error',
    warning: 'Warning',
    info: 'Information',
    success: 'Success'
  };

  return (
    <Alert 
      severity={severity} 
      sx={{ mb: 3, borderRadius: 2 }}
      action={
        <IconButton
          aria-label="close"
          color="inherit"
          size="small"
          onClick={onDismiss}
        >
          <Close fontSize="small" />
        </IconButton>
      }
    >
      <AlertTitle>{title || defaultTitles[severity]}</AlertTitle>
      {error}
    </Alert>
  );
};

export default ErrorBanner;
